/**
 * The ledger of website orders this device has already alarmed about.
 *
 * Two channels can announce the same order — the realtime beacon when the app
 * is awake, and a Web Push when it is not — so the alarm needs a single record
 * of what it has already shouted about. Persisting it also means a page reload
 * during a ring does not restart the alarm.
 *
 * Every operation is best-effort: a device with localStorage disabled or full
 * falls back to memory rather than throwing, because a broken ledger must never
 * take down the alarm itself.
 */

const STORAGE_KEY = 'komis_pos_alerted_website_orders';
const MAX_ENTRIES = 200;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Entries are [{ id, at }]. `at` is when we alerted, not when the order was
// placed — it only drives pruning.
let memoryEntries = [];
let memoryOnly = false;

function readEntries() {
  if (memoryOnly) return memoryEntries;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Disabled, full, or holding corrupt JSON — stop trying for this session.
    memoryOnly = true;
    return memoryEntries;
  }
}

function writeEntries(entries) {
  memoryEntries = entries;
  if (memoryOnly) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    memoryOnly = true;
  }
}

/** Drop malformed and stale entries, and cap the total size. */
function prune(entries, now) {
  const cutoff = now - MAX_AGE_MS;
  const valid = (Array.isArray(entries) ? entries : []).filter(
    (entry) => entry && entry.id != null && Number.isFinite(entry.at) && entry.at >= cutoff
  );
  return valid.length > MAX_ENTRIES ? valid.slice(valid.length - MAX_ENTRIES) : valid;
}

const toIdSet = (entries) => new Set(entries.map((entry) => String(entry.id)));

/**
 * Load the ledger, pruning as a side effect so it cannot grow without bound.
 */
export function loadAlertedOrderIds(now = Date.now()) {
  const pruned = prune(readEntries(), now);
  writeEntries(pruned);
  return toIdSet(pruned);
}

/**
 * Record ids as alerted and return the updated set.
 */
export function recordAlertedOrderIds(ids, now = Date.now()) {
  const incoming = (Array.isArray(ids) ? ids : [ids])
    .filter((id) => id != null)
    .map(String);

  const existing = prune(readEntries(), now);
  const known = toIdSet(existing);
  const added = incoming
    .filter((id) => !known.has(id))
    .map((id) => ({ id, at: now }));

  const pruned = prune(existing.concat(added), now);
  writeEntries(pruned);
  return toIdSet(pruned);
}

/** Test seam: clears both the stored ledger and the memory fallback state. */
export function __resetAlertedOrders() {
  memoryEntries = [];
  memoryOnly = false;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}
